/**
 * Utilidades para formatear y manipular horarios de atención
 */

/**
 * Formatea un horario JSON a texto legible para el usuario
 * @param {string|object} horarioData - Datos del horario en formato JSON string o objeto
 * @returns {string} - Texto formateado del horario
 */
export const formatearHorarioParaDisplay = (horarioData) => {
  if (!horarioData) return 'Sin horario definido';
  
  try {
    // Parseamos el JSON si es string
    const horarios = typeof horarioData === 'string' ? JSON.parse(horarioData) : horarioData;
    
    if (!horarios || typeof horarios !== 'object') {
      return 'Sin horario definido';
    }

    const diasSemana = [
      { key: 'lunes', label: 'Lun' },
      { key: 'martes', label: 'Mar' },
      { key: 'miercoles', label: 'Mié' },
      { key: 'jueves', label: 'Jue' },
      { key: 'viernes', label: 'Vie' },
      { key: 'sabado', label: 'Sáb' },
      { key: 'domingo', label: 'Dom' }
    ];

    const diasActivos = [];
    
    diasSemana.forEach(({ key, label }) => {
      const dia = horarios[key];
      if (!dia || !dia.activo) return;
      
      const turnos = [];
      
      // Agregar turno mañana si está activo
      if (dia.mañana && dia.mañana.activo && dia.mañana.inicio && dia.mañana.fin) {
        turnos.push(`${dia.mañana.inicio}-${dia.mañana.fin}`);
      }
      
      // Agregar turno tarde si está activo
      if (dia.tarde && dia.tarde.activo && dia.tarde.inicio && dia.tarde.fin) {
        turnos.push(`${dia.tarde.inicio}-${dia.tarde.fin}`);
      }
      
      if (turnos.length > 0) {
        diasActivos.push(`${label}: ${turnos.join(', ')}`);
      }
    });
    
    if (diasActivos.length === 0) {
      return 'Sin horario definido';
    }
    
    // Si hay muchos días, mostrar de forma más compacta
    if (diasActivos.length > 3) {
      return diasActivos.join(' | ');
    }
    
    return diasActivos.join(' • ');
    
  } catch (error) {
    console.error('Error al formatear horario:', error);
    return 'Error en formato de horario';
  }
};

/**
 * Genera un resumen compacto del horario (ej: "Lun-Vie 9-18")
 * @param {string|object} horarioData - Datos del horario en formato JSON string o objeto
 * @returns {string} - Resumen compacto del horario
 */
export const generarResumenHorario = (horarioData) => {
  if (!horarioData) return 'Sin horario';
  
  try {
    const horarios = typeof horarioData === 'string' ? JSON.parse(horarioData) : horarioData;
    
    if (!horarios || typeof horarios !== 'object') {
      return 'Sin horario';
    }

    const diasSemana = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
    const diasActivos = [];
    
    diasSemana.forEach(dia => {
      const diaData = horarios[dia];
      if (diaData && diaData.activo) {
        diasActivos.push(dia);
      }
    });
    
    if (diasActivos.length === 0) return 'Cerrado';
    
    // Detectar patrones comunes
    const esLunesViernes = diasActivos.length === 5 && 
      diasActivos.every(dia => ['lunes', 'martes', 'miercoles', 'jueves', 'viernes'].includes(dia));
    
    const esLunesSabado = diasActivos.length === 6 && 
      diasActivos.every(dia => ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'].includes(dia));
    
    const esTodosLosDias = diasActivos.length === 7;
    
    // Obtener horario representativo (del lunes o primer día activo)
    const primerDiaActivo = horarios[diasActivos[0]];
    const horarioTexto = [];
    
    if (primerDiaActivo.mañana && primerDiaActivo.mañana.activo) {
      horarioTexto.push(`${primerDiaActivo.mañana.inicio}-${primerDiaActivo.mañana.fin}`);
    }
    if (primerDiaActivo.tarde && primerDiaActivo.tarde.activo) {
      horarioTexto.push(`${primerDiaActivo.tarde.inicio}-${primerDiaActivo.tarde.fin}`);
    }
    
    const horarioStr = horarioTexto.join(', ');
    
    if (esTodosLosDias) {
      return `Todos los días ${horarioStr}`;
    } else if (esLunesSabado) {
      return `Lun-Sáb ${horarioStr}`;
    } else if (esLunesViernes) {
      return `Lun-Vie ${horarioStr}`;
    } else {
      // Para casos específicos, mostrar días individuales
      const diasCortos = {
        'lunes': 'Lun',
        'martes': 'Mar',
        'miercoles': 'Mié',
        'jueves': 'Jue',
        'viernes': 'Vie',
        'sabado': 'Sáb',
        'domingo': 'Dom'
      };
      
      const diasFormateados = diasActivos.map(dia => diasCortos[dia]).join(',');
      return `${diasFormateados} ${horarioStr}`;
    }
    
  } catch (error) {
    console.error('Error al generar resumen de horario:', error);
    return 'Error en horario';
  }
};

/**
 * Valida si un horario JSON es válido
 * @param {string|object} horarioData - Datos del horario en formato JSON string o objeto
 * @returns {boolean} - True si el horario es válido
 */
export const validarHorario = (horarioData) => {
  if (!horarioData) return false;
  
  try {
    const horarios = typeof horarioData === 'string' ? JSON.parse(horarioData) : horarioData;
    
    if (!horarios || typeof horarios !== 'object') return false;
    
    const diasRequeridos = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
    
    return diasRequeridos.every(dia => {
      const diaData = horarios[dia];
      return diaData && 
             typeof diaData.activo === 'boolean' &&
             diaData.mañana && 
             diaData.tarde &&
             typeof diaData.mañana.activo === 'boolean' &&
             typeof diaData.tarde.activo === 'boolean';
    });
    
  } catch (error) {
    return false;
  }
};